"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";

export type InboxPopoverTriggerProps = {
  href: string;
  label: string;
  className: string;
  children: ReactNode;
  onOpen: () => void;
};

export function InboxPopover({
  href,
  label,
  title,
  footerLabel,
  className,
  children,
  onOpen,
  renderContent,
}: InboxPopoverTriggerProps & {
  title?: string;
  footerLabel: string;
  renderContent: (onClose: () => void) => ReactNode;
}) {
  const tCommon = useTranslations("common");
  const pathname = usePathname();
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;

    closeRef.current?.focus({ preventScroll: true });
    const desktop = window.matchMedia("(min-width: 1024px)");

    function closeOnOutside(event: PointerEvent) {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    }

    function closeOnEscape(event: KeyboardEvent) {
      if (event.key !== "Escape") return;

      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus({ preventScroll: true });
    }

    function closeOnMobile() {
      if (!desktop.matches) setOpen(false);
    }

    document.addEventListener("pointerdown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    desktop.addEventListener("change", closeOnMobile);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
      desktop.removeEventListener("change", closeOnMobile);
    };
  }, [open]);

  return (
    <div
      ref={containerRef}
      className="hidden shrink-0 lg:block"
      onBlur={(event) => {
        if (event.relatedTarget instanceof Node && !event.currentTarget.contains(event.relatedTarget)) {
          setOpen(false);
        }
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label={label}
        title={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        className={className}
        onClick={() => {
          if (!open) onOpen();
          setOpen((value) => !value);
        }}
      >
        {children}
      </button>
      {open ? (
        <section
          id={panelId}
          role="dialog"
          aria-labelledby={`${panelId}-title`}
          className="absolute right-0 top-full z-50 flex max-h-[calc(100dvh-var(--workspace-controls-height)-var(--app-frame-inset)-var(--account-notice-height)-1rem)] w-96 flex-col overflow-hidden rounded-l-2xl border border-border bg-surface text-text shadow-dropdown"
        >
          <div className="flex shrink-0 items-center justify-between gap-3 px-4 pb-3 pt-4">
            <h2 id={`${panelId}-title`} className="text-2xl font-bold tracking-tight">{title ?? label}</h2>
            <button
              ref={closeRef}
              type="button"
              aria-label={tCommon("close")}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-text-muted outline-none hover:bg-surface-hover hover:text-text focus-visible:ring-2 focus-visible:ring-primary"
              onClick={() => {
                setOpen(false);
                triggerRef.current?.focus({ preventScroll: true });
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="h-4 w-4" aria-hidden="true">
                <path strokeLinecap="round" d="m6 6 12 12M6 18 18 6" />
              </svg>
            </button>
          </div>
          {renderContent(() => setOpen(false))}
          <Link
            href={href}
            prefetch={false}
            onClick={() => setOpen(false)}
            className="flex shrink-0 items-center justify-center gap-2 border-t border-border px-4 py-3.5 text-sm font-semibold text-primary outline-none hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:underline"
          >
            {footerLabel}
            <span aria-hidden="true">→</span>
          </Link>
        </section>
      ) : null}
    </div>
  );
}
